import React, { useEffect, useState } from 'react'
import {
  Modal,
  Upload,
  Button,
  notification,
  Spin,
  Divider,
  DatePicker,
  Input,
  Tooltip,
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { API } from 'src/utils/interceptor'
import { uploadFile } from 'src/utils/uploadFile'
import {
  createXeroBill,
  fetchReceiptingOrders,
  fetchReceiptingProducts,
  fetchXeroProducts,
} from 'src/features/ReceiptingSlice'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import config from 'src/config'
import SelectedPreviews from './components/SelectedPreviews'
import AttachedInvoicePreview from './components/AttachedInvoicePreview'
// import imageCompression from 'browser-image-compression'
import {
  getXeroAutoSendFailureNotice,
  getXeroAutoSendSkipNotice,
  getXeroAutoSendSuccessNotice,
  hasXeroSupplierDefaults,
  readXeroAutoSendEnabled,
  readXeroAutoSendEnabledForSupplier,
} from 'src/utils/xeroAutoSendCopy'
import {
  clearXeroData,
  fetchXeroOrganizationData,
} from 'src/features/XeroIntegrationSlice'
import SelectFromOMS from './components/SelectFromOMS'
import AttachInvoiceNumberInput from './components/AttachInvoiceNumberInput'
import GoodsReceiptingAttachConfirmModal from './components/GoodsReceiptingAttachConfirmModal'
import { getPrice, getQuantity } from 'src/utils/priceFunctions'
import {
  buildEmptyUploadFileFields,
  formatPenniesAsPoundsInput,
  getInvoicePreviewUrl,
  INVOICE_TOTAL_SOURCE,
  parseInvoiceTotalToPennies,
  resolveInvoiceDateForAttach,
  resolveInvoiceNumberForAttach,
} from 'src/utils/invoiceUploadHelpers'
import { prefillUploadFileFromOrder } from 'src/utils/goodsReceiptingAttachHelpers'
import { getUploadedInvoices } from 'src/features/NewInvoicesSlice'
import { resolveXeroOrganizationId } from 'src/utils/xeroOrganization'
// Xero bill helpers — supplier invoice # → InvoiceNumber/Reference (not OMS PO alone)
import {
  resolveXeroSupplierInvoiceNumber,
  withXeroSupplierInvoiceNumber,
  buildXeroBillLineItems,
  planXeroExportJobs,
  partitionFinanceDocuments,
} from 'src/utils/xeroAutoSendBill'
import { getLinkedInvoicesForReceiptingOrder } from 'src/utils/reconciliationHealth'
import {
  OLLIE_FINDINGS_WRITE_EVENTS,
  ollieFindingsSaved,
} from 'src/utils/ollieFindingsRefresh'

const AttachInvoiceModal = ({ open, order, onClose }) => {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [localPreviews, setLocalPreviews] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [fileDataUrl, setFileDataUrl] = useState('')
  const [isReplacing, setIsReplacing] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  // const [isDownloading, setIsDownloading] = useState(false)
  const dateFormat = 'DD/MM/YYYY'
  const filterDates = useSelector(state => state.receipting.filterDates)
  const { _id: venueId, xeroConnection } = useSelector(
    state => state.venue?.venue,
  )
  const automaticSendToXeroRedux = readXeroAutoSendEnabled(
    useSelector(state => state.venue?.venue || {}),
  )
  const dispatch = useDispatch()
  const [isReplacingInvoice, setIsReplacingInvoice] = useState(false)
  // const [message, setMessage] = useState('')
  const xeroProducts = useSelector(state => state.receipting.xeroProducts)
  const xeroIntegration = useSelector(state => state.xeroIntegration) || {
    accounts: [],
    contacts: [],
    accountsLoading: false,
    contactsLoading: false,
    isLoading: false,
    error: null,
  }
  const selectedXeroAccountId = order?.venueSupplier?.defaultAccount
  const selectedXeroContact = order?.venueSupplier?.defaultContact
  const selectedXeroAccount = xeroIntegration.accounts.find(
    account => account.AccountID === selectedXeroAccountId,
  )
  const receptingOrder = xeroProducts
    ?.flat()
    .filter(order => order.supplierConfirmed === true)
  const sentToXero = order?.sentToXero || false
  const supplierAutoSendEnabled = readXeroAutoSendEnabledForSupplier(
    order?.venueSupplier,
  )
  const [selectedInvoices, setSelectedInvoices] = useState([])
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(null)
  const [invoiceTotalInput, setInvoiceTotalInput] = useState('')
  const [invoiceTotalSource, setInvoiceTotalSource] = useState(null)
  const [confirmUploadOpen, setConfirmUploadOpen] = useState(false)
  const [confirmUploadFiles, setConfirmUploadFiles] = useState([])
  const [orderLinesForConfirm, setOrderLinesForConfirm] = useState([])
  const venue = useSelector(state => state.venue.venue)
  const uploadedInvoices = useSelector(
    state => state.newInvoices?.uploadedInvoices || [],
  )
  const receiptingProducts = useSelector(
    state => state.receipting.receiptingProducts,
  )
  const resolvedOrderIds = Array.isArray(order?.orderIds)
    ? order.orderIds.filter(Boolean)
    : order?._id
    ? [order._id]
    : []

  const mappedOrders = receptingOrder?.map(item => ({
    ...item,
    price:
      item?.receipting?.updatedPrice != null
        ? item.receipting.updatedPrice
        : getPrice(item),
    quantity: getQuantity(item),
  }))

  const loadOrderLinesForConfirm = async () => {
    const supplierId = order?.supplier?._id
    const po = order?.purchaseOrderNumber
    if (!venueId || !supplierId || !po) {
      setOrderLinesForConfirm([])
      return []
    }

    const cached = (receiptingProducts || []).find(
      item =>
        String(item?.supplier?._id || item?.supplier) === String(supplierId) &&
        item?.purchaseOrderNumber === po,
    )
    if (cached?.orders?.length) {
      setOrderLinesForConfirm(cached.orders)
      return cached.orders
    }

    try {
      const result = await dispatch(
        fetchReceiptingProducts(venueId, supplierId, po),
      )
      const lines = result?.orders || []
      setOrderLinesForConfirm(lines)
      return lines
    } catch (error) {
      console.error('Could not load order lines for GR attach confirm', error)
      setOrderLinesForConfirm([])
      return []
    }
  }

  const handleMultipleFileSelect = async ({ fileList }) => {
    const MAX_SIZE = 10 * 1024 * 1024
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf']
    const files = []

    fileList.forEach(fileWrap => {
      const f = fileWrap.originFileObj || fileWrap
      if (!f) return
      if (f.size > MAX_SIZE) {
        notification.error({ message: `${f.name} must be smaller than 10MB.` })
        return
      }
      if (!validTypes.includes(f.type)) {
        notification.error({
          message: `${f.name} must be a JPEG, PNG, or PDF.`,
        })
        return
      }
      files.push(f)
    })

    if (!files.length) return

    const readers = files.map(
      file =>
        new Promise(resolve => {
          const reader = new FileReader()
          reader.onload = e => {
            resolve(
              prefillUploadFileFromOrder(
                buildEmptyUploadFileFields(file.name, e.target.result),
                order,
              ),
            )
          }
          reader.readAsDataURL(file)
        }),
    )

    try {
      const uploadFiles = await Promise.all(readers)
      setSelectedInvoices([])
      setLocalPreviews([])
      setSelectedFiles([])
      setFileDataUrl('')
      await loadOrderLinesForConfirm()
      setConfirmUploadFiles(uploadFiles)
      setConfirmUploadOpen(true)
    } catch (error) {
      console.error('Could not prepare invoice files for upload', error)
      notification.error({
        message: 'Could not read invoice file',
        description: 'Please try selecting the file again.',
      })
    }
  }
  // Fetch data for default organization if only one exists
  useEffect(() => {
    if (xeroConnection && xeroConnection?.organizations?.length) {
      const organizationId = resolveXeroOrganizationId(xeroConnection)
      if (!organizationId) return
      dispatch(fetchXeroOrganizationData(organizationId, venueId))
    }
  }, [xeroConnection, venueId])

  // Cleanup Redux state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearXeroData())
    }
  }, [dispatch])

  useEffect(() => {
    if (
      automaticSendToXeroRedux &&
      supplierAutoSendEnabled &&
      xeroConnection &&
      selectedXeroAccount &&
      selectedXeroContact
    ) {
      dispatch(
        fetchXeroProducts(
          venueId,
          order?.supplier?._id,
          order?.purchaseOrderNumber,
        ),
      )
    }
  }, [order])

  useEffect(() => {
    if (order?.attachedInvoicesUrls?.length > 0) {
      setIsReplacingInvoice(true)
    } else {
      setIsReplacingInvoice(false)
    }
  }, [order?.attachedInvoicesUrls])

  // Initialize invoice number and date from Invoice documents when modal opens
  useEffect(() => {
    if (open && order) {
      const loadInvoiceData = async () => {
        try {
          // Try to fetch invoice data from attached invoice URLs
          // Use first attached invoice URL to get invoiceNumber/invoiceDate
          const attachedUrlsList = order?.attachedInvoicesUrls || []
          const invoiceUrlToFetch =
            order.invoiceUrl ||
            (attachedUrlsList && attachedUrlsList.length > 0
              ? attachedUrlsList[0]
              : null)

          if (invoiceUrlToFetch) {
            try {
              const invoiceResponse = await API.get(
                'api2',
                'invoices/getInvoicebyUrl',
                {
                  queryStringParameters: { invoiceUrl: invoiceUrlToFetch },
                },
              )
              if (invoiceResponse?.success && invoiceResponse?.data) {
                const invoice = invoiceResponse.data
                setInvoiceNumber(invoice.invoiceNumber || '')
                setInvoiceDate(
                  invoice.invoiceDate ? moment(invoice.invoiceDate) : null,
                )
                return
              }
            } catch (err) {
              // If fetching by URL fails, continue to fallback
              console.warn('Could not fetch invoice by URL:', err)
            }
          }

          // Fallback: try to get from order object (if included in receipting orders response)
          if (order.invoiceNumber || order.invoiceDate) {
            setInvoiceNumber(order.invoiceNumber || '')
            setInvoiceDate(order.invoiceDate ? moment(order.invoiceDate) : null)
            return
          }

          // Default: empty
          setInvoiceNumber('')
          setInvoiceDate(null)
          setInvoiceTotalInput('')
          setInvoiceTotalSource(null)
        } catch (error) {
          console.error('Error loading invoice data:', error)
          setInvoiceNumber('')
          setInvoiceDate(null)
          setInvoiceTotalInput('')
          setInvoiceTotalSource(null)
        }
      }

      loadInvoiceData()
    } else {
      // Reset when modal closes
      setInvoiceNumber('')
      setInvoiceDate(null)
      setInvoiceTotalInput('')
      setInvoiceTotalSource(null)
      setConfirmUploadOpen(false)
      setConfirmUploadFiles([])
    }
  }, [open, order])

  useEffect(() => {
    if (open && venueId) {
      dispatch(getUploadedInvoices(venueId))
    }
  }, [open, venueId, dispatch])

  useEffect(() => {
    if (!selectedInvoices?.length) {
      return
    }

    const [firstSelectedInvoice] = selectedInvoices
    const resolvedNumber = resolveInvoiceNumberForAttach(
      firstSelectedInvoice,
      order,
    )
    const resolvedDate = resolveInvoiceDateForAttach(
      firstSelectedInvoice,
      order,
    )

    if (resolvedNumber) {
      setInvoiceNumber(resolvedNumber)
    }

    if (resolvedDate) {
      setInvoiceDate(moment(resolvedDate))
    }
  }, [selectedInvoices, order])

  const applySelectedInvoices = invoices => {
    if (!invoices?.length) {
      return
    }

    setSelectedInvoices(invoices)
    setSelectedFiles([])
    setLocalPreviews([])
    setFileDataUrl('')

    const [firstSelectedInvoice] = invoices
    const resolvedNumber = resolveInvoiceNumberForAttach(
      firstSelectedInvoice,
      order,
    )
    const resolvedDate = resolveInvoiceDateForAttach(
      firstSelectedInvoice,
      order,
    )

    if (resolvedNumber) {
      setInvoiceNumber(resolvedNumber)
    }

    if (resolvedDate) {
      setInvoiceDate(moment(resolvedDate))
    }

    const existingTotal =
      firstSelectedInvoice?.invoiceTotal ??
      firstSelectedInvoice?.invoiceTotalPennies
    if (existingTotal != null && Number.isFinite(Number(existingTotal))) {
      setInvoiceTotalInput(formatPenniesAsPoundsInput(existingTotal))
      setInvoiceTotalSource(INVOICE_TOTAL_SOURCE.MANUAL)
    }
  }

  const handleSelectInvoiceFromNumber = invoice => {
    applySelectedInvoices([invoice])
  }

  const getEffectiveInvoiceNumber = () => {
    if (invoiceNumber?.trim()) {
      return invoiceNumber.trim()
    }

    if (selectedInvoices?.[0]) {
      return resolveInvoiceNumberForAttach(selectedInvoices[0], order)
    }

    return ''
  }

  const getEffectiveInvoiceDate = () => {
    if (invoiceDate) {
      return invoiceDate
    }

    if (selectedInvoices?.[0]) {
      const resolvedDate = resolveInvoiceDateForAttach(
        selectedInvoices[0],
        order,
      )
      return resolvedDate ? moment(resolvedDate) : null
    }

    return null
  }

  const handleAttachOrReplaceInvoice = async () => {
    const hasLocal = selectedFiles && selectedFiles.length > 0
    const hasOMS = selectedInvoices && selectedInvoices.length > 0

    if (!hasLocal && !hasOMS) {
      notification.error({ message: 'Please select a file first.' })
      return
    }

    // Validate required fields
    const effectiveInvoiceNumber = getEffectiveInvoiceNumber()
    const effectiveInvoiceDate = getEffectiveInvoiceDate()

    if (!effectiveInvoiceNumber) {
      notification.error({
        message: 'Invoice Number Required',
        description: 'Please enter the supplier invoice number.',
      })
      return
    }

    if (!effectiveInvoiceDate) {
      notification.error({
        message: 'Invoice Date Required',
        description: 'Please select the invoice date.',
      })
      return
    }

    if (!resolvedOrderIds.length) {
      notification.error({
        message: 'Unable to attach invoice',
        description:
          'This order is missing order IDs. Please refresh and try again.',
      })
      return
    }

    const parsedInvoiceTotal = parseInvoiceTotalToPennies(invoiceTotalInput)
    if (hasOMS && parsedInvoiceTotal == null) {
      notification.error({
        message: 'Invoice Total Required',
        description:
          'Enter the invoice total including VAT so we can compare it to the order.',
      })
      return
    }

    setIsLoading(true)

    try {
      let uploadedKeys = []

      if (hasLocal) {
        for (const file of selectedFiles) {
          const uploaded = await uploadFile(file)
          if (uploaded?.keyName)
            uploadedKeys.push({
              keyName: uploaded.keyName,
              fileName: file.name,
            })
        }
      }

      const omsInvoiceIds = hasOMS
        ? selectedInvoices.map(inv => inv.id).filter(Boolean)
        : []
      const invoiceKeyNames = uploadedKeys
        .map(file => file?.keyName)
        .filter(Boolean)

      const totalFields =
        parsedInvoiceTotal == null
          ? {}
          : {
              invoiceTotal: parsedInvoiceTotal,
              invoiceTotalSource:
                invoiceTotalSource || INVOICE_TOTAL_SOURCE.MANUAL,
            }

      await API.put('api2', 'orders/attachInvoice', {
        body: {
          orderIds: resolvedOrderIds,
          // Keep both payload shapes for backend compatibility.
          // Some environments still expect singular keys.
          invoiceKeys: invoiceKeyNames,
          invoiceFiles: uploadedKeys,
          invoiceKey: invoiceKeyNames[0] || '',
          invoiceKeyNames,
          selectedInvoices: omsInvoiceIds,
          selectedInvoice: omsInvoiceIds[0] || '',
          venueId,
          invoiceNumber: effectiveInvoiceNumber,
          invoiceDate: effectiveInvoiceDate.toISOString(),
          appendToExisting: !!isAdding,
          ...totalFields,
        },
      })
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
      })

      notification.success({
        message: isAdding
          ? 'Invoice added successfully!'
          : order?.invoiceUrl
          ? 'Invoice replaced successfully!'
          : 'Invoice attached successfully!',
      })
      dispatch(
        fetchReceiptingOrders(
          venueId,
          moment(filterDates.start).format(dateFormat),
          moment(filterDates.end).format(dateFormat),
        ),
      )
      if (venueId) {
        dispatch(getUploadedInvoices(venueId))
      }

      if (selectedXeroAccount && selectedXeroAccount?.Type === 'REVENUE') {
        notification.error({
          message:
            'You cannot create a bill using a Revenue account in Xero. Please select a different account or change this account’s type in Xero (for example, to an Expense account).',
        })
        onClose()
        return
      }

      const isCreditNote = order?.totalSpendReceived < 0
      const linkedDocs = getLinkedInvoicesForReceiptingOrder(
        order,
        uploadedInvoices,
      )
      const selectedDocIds = new Set(
        (selectedInvoices || []).map(inv => String(inv?.id || inv?._id)),
      )
      const financeDocuments = [
        ...linkedDocs.filter(
          doc => !selectedDocIds.has(String(doc?.id || doc?._id)),
        ),
        ...(selectedInvoices || []),
      ]
      const { invoices: linkedInvoices, creditNotes: linkedCredits } =
        partitionFinanceDocuments(financeDocuments)
      const hasDualDocuments =
        linkedInvoices.length > 0 && linkedCredits.length > 0
      const hasPendingDualExport =
        hasDualDocuments &&
        financeDocuments.some(
          doc => !(doc?.xeroInvoiceId || doc?.status === 'sentToXero'),
        )

      const autoSendSkipNotice = supplierAutoSendEnabled
        ? getXeroAutoSendSkipNotice({
            autoSendEnabled: automaticSendToXeroRedux,
            xeroConnected: Boolean(xeroConnection?.accessToken),
            xeroExpired: Boolean(venue?.xeroConnectionExpired),
            hasSupplierDefaults: hasXeroSupplierDefaults(order?.venueSupplier),
            alreadySentToXero: Boolean(sentToXero) && !hasPendingDualExport,
            completedActionLabel: 'Attached',
          })
        : null
      if (autoSendSkipNotice && !isReplacingInvoice) {
        notification.warning({
          message: autoSendSkipNotice.message,
          description: autoSendSkipNotice.description,
          duration: 8,
        })
      }

      if (
        automaticSendToXeroRedux &&
        supplierAutoSendEnabled &&
        xeroConnection &&
        selectedXeroAccount &&
        selectedXeroContact &&
        !isReplacingInvoice &&
        (!sentToXero || hasPendingDualExport)
      ) {
        try {
          const accountId = selectedXeroAccount?.AccountID
          if (!accountId || !selectedXeroContact) {
            throw new Error('Select a Xero account and contact before sending.')
          }
          const organizationId = resolveXeroOrganizationId(xeroConnection)
          const xeroNotifyOpts = {
            redirectOnSuccess: false,
            notifyOnSuccess: false,
            notifyOnError: false,
            rejectOnError: true,
          }

          // Helper: convert a remote OMS file URL to a File
          const downloadUrlToFile = async fileUrl => {
            const response = await fetch(fileUrl)
            const blob = await response.blob()
            const filenameFromUrl = decodeURIComponent(
              fileUrl.split('/').pop() || 'invoice',
            )
            return new File([blob], filenameFromUrl, {
              type: blob.type || 'application/octet-stream',
            })
          }

          const plan = planXeroExportJobs({
            financeDocuments,
            venue,
            order,
            venueSupplier: order?.venueSupplier,
            receiptingProducts: mappedOrders || [],
            xeroAccounts: xeroIntegration?.accounts || [],
            supplierDefaultAccountId: accountId,
            selectedContactId:
              selectedXeroContact?.ContactID || selectedXeroContact || null,
            invoice: selectedInvoices?.[0],
            invoiceNumber: effectiveInvoiceNumber,
          })

          let sentToXeroBill = false

          if (plan.mode === 'documents' && plan.jobs.length > 0) {
            let latestBillXeroId = null
            for (const job of plan.jobs) {
              if (job.billData) {
                job.billData.Contact = { ContactID: selectedXeroContact }
              }
              if (
                job.documentType === 'creditNote' &&
                job.billData &&
                latestBillXeroId &&
                !job.billData.allocateToXeroInvoiceId
              ) {
                job.billData.allocateToXeroInvoiceId = latestBillXeroId
              }
              const docFiles = []
              const jobDoc = financeDocuments.find(
                d => String(d?.id || d?._id) === String(job.selectedInvoiceId),
              )
              if (jobDoc) {
                const url = getInvoicePreviewUrl(
                  jobDoc,
                  config.apiGateway.EXPRESS_URL,
                )
                if (url) {
                  docFiles.push(await downloadUrlToFile(url))
                }
              }
              if (
                docFiles.length === 0 &&
                selectedFiles &&
                selectedFiles.length > 0
              ) {
                docFiles.push(...selectedFiles)
              }
              const result = await dispatch(
                createXeroBill(
                  job.billData,
                  docFiles,
                  venueId,
                  organizationId,
                  resolvedOrderIds,
                  job.selectedInvoiceId || '',
                  xeroNotifyOpts,
                ),
              )
              if (job.documentType === 'invoice' && result?.xeroInvoiceId) {
                latestBillXeroId = result.xeroInvoiceId
              }
              sentToXeroBill = true
            }
          } else {
            const Lineitems = buildXeroBillLineItems({
              rows: mappedOrders || [],
              venueSupplier: order?.venueSupplier,
              venue,
              xeroAccounts: xeroIntegration?.accounts || [],
              supplierDefaultAccountId: accountId,
              isCreditNote,
            }).map(line => ({
              ...line,
              TaxType:
                selectedXeroAccount?.TaxType === 'OUTPUT2' ? '' : line.TaxType,
            }))
            const Contact = {
              ContactID: selectedXeroContact,
            }
            const billData = withXeroSupplierInvoiceNumber(
              {
                Type: isCreditNote ? 'ACCPAYCREDIT' : 'ACCPAY',
                Contact,
                Date: dayjs(order?.orderedDate).format('YYYY-MM-DD'),
                DueDate: dayjs(order?.orderedDate).format('YYYY-MM-DD'),
                LineAmountTypes: 'Exclusive',
                LineItems: Lineitems,
                Status: 'AUTHORISED',
              },
              resolveXeroSupplierInvoiceNumber({
                invoiceNumber: effectiveInvoiceNumber,
                order,
              }),
              {
                purchaseOrderNumber: order?.purchaseOrderNumber,
                venueTitle: venue?.title,
              },
            )
            const orderIds = resolvedOrderIds
            const invoiceIdForXero =
              selectedInvoices?.[0]?._id || selectedInvoices?.[0]?.id || ''

            // Send local selected files
            if (selectedFiles && selectedFiles.length > 0) {
              await dispatch(
                createXeroBill(
                  billData,
                  selectedFiles,
                  venueId,
                  organizationId,
                  orderIds,
                  invoiceIdForXero,
                  xeroNotifyOpts,
                ),
              )
              sentToXeroBill = true
            }

            // Send OMS-selected invoices by downloading and forwarding as File(s)
            if (selectedInvoices && selectedInvoices.length > 0) {
              const omsFiles = []
              for (const inv of selectedInvoices) {
                const url = getInvoicePreviewUrl(
                  inv,
                  config.apiGateway.EXPRESS_URL,
                )
                if (!url) {
                  continue
                }
                const fileFromOms = await downloadUrlToFile(url)
                omsFiles.push(fileFromOms)
              }
              if (omsFiles.length > 0) {
                await dispatch(
                  createXeroBill(
                    billData,
                    omsFiles,
                    venueId,
                    organizationId,
                    orderIds,
                    invoiceIdForXero,
                    xeroNotifyOpts,
                  ),
                )
                sentToXeroBill = true
              }
            }
          }

          if (sentToXeroBill) {
            notification.success({
              ...getXeroAutoSendSuccessNotice('Attached'),
              duration: 5,
            })
          }
        } catch (xeroError) {
          console.error(xeroError)
          const notice = getXeroAutoSendFailureNotice(
            xeroError?.message,
            'Attached',
          )
          notification.warning({
            ...notice,
            duration: 8,
          })
        }
      }
      onClose()
    } catch (error) {
      console.error(error)
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message
      notification.error({
        message: 'Error uploading file.',
        description: backendMessage || 'Unknown server error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadUrl = async (url, filename = 'Invoice-OMS') => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
    } catch (err) {
      notification.error({ message: 'Failed to download invoice' })
      console.error(err)
    }
  }
  const attachedUrls = order?.attachedInvoicesUrls || []
  const hasSelectedInvoiceFile =
    (selectedInvoices && selectedInvoices.length > 0) ||
    (localPreviews && localPreviews.length > 0) ||
    (selectedFiles && selectedFiles.length > 0)
  const effectiveInvoiceNumber = getEffectiveInvoiceNumber()
  const effectiveInvoiceDate = getEffectiveInvoiceDate()
  const omsNeedsTotal =
    selectedInvoices &&
    selectedInvoices.length > 0 &&
    parseInvoiceTotalToPennies(invoiceTotalInput) == null
  const isAttachDisabled =
    !hasSelectedInvoiceFile ||
    isLoading ||
    !effectiveInvoiceNumber ||
    !effectiveInvoiceDate ||
    omsNeedsTotal

  const getAttachDisabledReason = () => {
    if (isLoading) {
      return ''
    }

    if (!hasSelectedInvoiceFile) {
      return 'Select an invoice file or choose one from OMS storage.'
    }

    if (!effectiveInvoiceNumber) {
      return 'Enter the supplier invoice number printed on the document.'
    }

    if (!effectiveInvoiceDate) {
      return 'Select the invoice date.'
    }

    if (omsNeedsTotal) {
      return 'Enter the invoice total including VAT.'
    }

    return ''
  }

  return (
    <>
      <Modal
        visible={open && !confirmUploadOpen}
        title={`${
          order?.invoiceUrl || order?.attachedInvoicesUrls?.length > 0
            ? 'Replace'
            : 'Attach'
        } Invoice for ${order?.supplier?.title} ${order?.purchaseOrderNumber}`}
        onCancel={() => {
          if (confirmUploadOpen) return
          onClose()
        }}
        footer={null}
        destroyOnClose
        width={1300}
        height={1000}
        bodyStyle={{ maxHeight: '85vh', overflowY: 'auto' }}
      >
        <p>
          Upload the supplier invoice for this order. Once attached, it will be
          visible to your team here in the Goods Receipting screen and will be
          included when sending to Xero
        </p>
        {order?.invoiceUrl && !attachedUrls?.length > 0 && !isReplacing && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <p style={{ alignSelf: 'flex-start', fontWeight: 500 }}>
              Currently Attached Invoice:
            </p>

            <div
              style={{
                width: '100%',
                display: 'flex',
                gap: 24,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1 }}>
                <AttachedInvoicePreview
                  url={order?.invoiceUrl}
                  alt='Current Invoice'
                  onDownload={activeUrl =>
                    handleDownloadUrl(
                      activeUrl || order.invoiceUrl,
                      `Invoice-${order?.purchaseOrderNumber || 'OMS'}`,
                    )
                  }
                />
              </div>
              <div
                style={{
                  flex: 0,
                  minWidth: 280,
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <h4 style={{ marginTop: 0, marginBottom: 16 }}>
                  Invoice Details <span style={{ color: '#ff4d4f' }}>*</span>
                </h4>
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      marginBottom: 4,
                      fontWeight: 500,
                    }}
                  >
                    Supplier Invoice Number{' '}
                    <span style={{ color: '#ff4d4f' }}>*</span>
                  </label>
                  <Input
                    value={invoiceNumber}
                    onChange={e => setInvoiceNumber(e.target.value)}
                    placeholder='Enter supplier invoice number'
                    status={
                      !invoiceNumber || !invoiceNumber.trim() ? 'error' : ''
                    }
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      marginBottom: 4,
                      fontWeight: 500,
                    }}
                  >
                    Invoice Date <span style={{ color: '#ff4d4f' }}>*</span>
                  </label>
                  <DatePicker
                    value={invoiceDate}
                    onChange={value => setInvoiceDate(value)}
                    format='DD/MM/YYYY'
                    style={{ width: '100%' }}
                    status={!invoiceDate ? 'error' : ''}
                  />
                </div>
                <Button
                  type='primary'
                  onClick={async () => {
                    if (!invoiceNumber || !invoiceNumber.trim()) {
                      notification.error({
                        message: 'Invoice Number Required',
                        description:
                          'Please enter the supplier invoice number.',
                      })
                      return
                    }
                    if (!invoiceDate) {
                      notification.error({
                        message: 'Invoice Date Required',
                        description: 'Please select the invoice date.',
                      })
                      return
                    }
                    setIsLoading(true)
                    try {
                      // Update invoice metadata without replacing the file
                      await API.put('api2', 'orders/attachInvoice', {
                        body: {
                          orderIds: resolvedOrderIds,
                          venueId,
                          invoiceNumber: invoiceNumber.trim(),
                          invoiceDate: invoiceDate.toISOString(),
                        },
                      })
                      notification.success({
                        message: 'Invoice details updated successfully!',
                      })
                      dispatch(
                        fetchReceiptingOrders(
                          venueId,
                          moment(filterDates.start).format(dateFormat),
                          moment(filterDates.end).format(dateFormat),
                        ),
                      )
                      if (venueId) {
                        dispatch(getUploadedInvoices(venueId))
                      }
                    } catch (error) {
                      console.error(error)
                      notification.error({
                        message: 'Error updating invoice details.',
                      })
                    } finally {
                      setIsLoading(false)
                    }
                  }}
                  style={{ marginTop: 16, width: '100%' }}
                  loading={isLoading}
                  disabled={
                    !invoiceNumber || !invoiceNumber.trim() || !invoiceDate
                  }
                >
                  Save Invoice Details
                </Button>
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
              <Button type='default' onClick={() => setIsReplacing(true)}>
                Replace Invoice
              </Button>
            </div>
          </div>
        )}

        {!order?.invoiceUrl &&
          attachedUrls?.length > 0 &&
          !isReplacing &&
          !isAdding && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
              }}
            >
              <p style={{ alignSelf: 'flex-start', fontWeight: 500 }}>
                Currently Attached Invoices:
              </p>

              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: 24,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: 16,
                  }}
                >
                  {attachedUrls?.map((url, index) => (
                    <AttachedInvoicePreview
                      key={index}
                      url={url}
                      alt={`Invoice ${index + 1}`}
                      onDownload={activeUrl =>
                        handleDownloadUrl(
                          activeUrl || url,
                          `Invoice-${order?.purchaseOrderNumber || 'OMS'}-${
                            index + 1
                          }`,
                        )
                      }
                    />
                  ))}
                </div>
                <div
                  style={{
                    flex: 0,
                    minWidth: 280,
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: 16,
                  }}
                >
                  <h4 style={{ marginTop: 0, marginBottom: 16 }}>
                    Invoice Details <span style={{ color: '#ff4d4f' }}>*</span>
                  </h4>
                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 12,
                        marginBottom: 4,
                        fontWeight: 500,
                      }}
                    >
                      Supplier Invoice Number{' '}
                      <span style={{ color: '#ff4d4f' }}>*</span>
                    </label>
                    <Input
                      value={invoiceNumber}
                      onChange={e => setInvoiceNumber(e.target.value)}
                      placeholder='Enter supplier invoice number'
                      status={
                        !invoiceNumber || !invoiceNumber.trim() ? 'error' : ''
                      }
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 12,
                        marginBottom: 4,
                        fontWeight: 500,
                      }}
                    >
                      Invoice Date <span style={{ color: '#ff4d4f' }}>*</span>
                    </label>
                    <DatePicker
                      value={invoiceDate}
                      onChange={value => setInvoiceDate(value)}
                      format='DD/MM/YYYY'
                      style={{ width: '100%' }}
                      status={!invoiceDate ? 'error' : ''}
                    />
                  </div>
                  <Button
                    type='primary'
                    onClick={async () => {
                      if (!invoiceNumber || !invoiceNumber.trim()) {
                        notification.error({
                          message: 'Invoice Number Required',
                          description:
                            'Please enter the supplier invoice number.',
                        })
                        return
                      }
                      if (!invoiceDate) {
                        notification.error({
                          message: 'Invoice Date Required',
                          description: 'Please select the invoice date.',
                        })
                        return
                      }
                      setIsLoading(true)
                      try {
                        // Update invoice metadata without replacing the file
                        await API.put('api2', 'orders/attachInvoice', {
                          body: {
                            orderIds: resolvedOrderIds,
                            venueId,
                            invoiceNumber: invoiceNumber.trim(),
                            invoiceDate: invoiceDate.toISOString(),
                          },
                        })
                        notification.success({
                          message: 'Invoice details updated successfully!',
                        })
                        dispatch(
                          fetchReceiptingOrders(
                            venueId,
                            moment(filterDates.start).format(dateFormat),
                            moment(filterDates.end).format(dateFormat),
                          ),
                        )
                        if (venueId) {
                          dispatch(getUploadedInvoices(venueId))
                        }
                      } catch (error) {
                        console.error(error)
                        notification.error({
                          message: 'Error updating invoice details.',
                        })
                      } finally {
                        setIsLoading(false)
                      }
                    }}
                    style={{ marginTop: 16, width: '100%' }}
                    loading={isLoading}
                    disabled={
                      !invoiceNumber || !invoiceNumber.trim() || !invoiceDate
                    }
                  >
                    Save Invoice Details
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                <Button type='default' onClick={() => setIsReplacing(true)}>
                  Replace Invoice
                </Button>
                <Button type='primary' onClick={() => setIsAdding(true)}>
                  Add another invoice or credit note
                </Button>
              </div>
            </div>
          )}

        {((!order?.invoiceUrl && !order?.attachedInvoicesUrls?.length > 0) ||
          isReplacing ||
          isAdding) && (
          <>
            {(isReplacing || isAdding) && (
              <div
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <Button
                  type='default'
                  onClick={() => {
                    setIsReplacing(false)
                    setIsAdding(false)
                  }}
                >
                  Back
                </Button>
                <span style={{ fontWeight: 500 }}>
                  {isAdding
                    ? 'Add another invoice or credit note'
                    : 'Replace invoice'}
                </span>
              </div>
            )}
            <Divider />

            {/* Invoice Details - Required Fields */}
            <div
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 24,
                marginBottom: 24,
                background: '#fafafa',
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>
                Invoice Details <span style={{ color: '#ff4d4f' }}>*</span>
              </h3>
              <p style={{ fontSize: 12, color: '#595959', marginBottom: 16 }}>
                Search uploaded invoices by number or supplier, or type the
                number from the document. Invoice date and total (including VAT)
                are required when attaching from OMS storage. Uploading from
                your computer opens a confirm step with order details
                pre-filled.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 16,
                }}
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      marginBottom: 4,
                      fontWeight: 500,
                    }}
                  >
                    Supplier Invoice Number{' '}
                    <span style={{ color: '#ff4d4f' }}>*</span>
                  </label>
                  <AttachInvoiceNumberInput
                    value={invoiceNumber}
                    onChange={setInvoiceNumber}
                    onSelectInvoice={handleSelectInvoiceFromNumber}
                    defaultSupplierTitle={order?.supplier?.title}
                    order={order}
                    placeholder='Search or enter supplier invoice number'
                  />
                  <p
                    style={{
                      fontSize: 11,
                      color: '#8c8c8c',
                      margin: '6px 0 0',
                    }}
                  >
                    Pick a matching uploaded invoice or type the number
                    manually.
                  </p>
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      marginBottom: 4,
                      fontWeight: 500,
                    }}
                  >
                    Invoice Date <span style={{ color: '#ff4d4f' }}>*</span>
                  </label>
                  <DatePicker
                    value={invoiceDate}
                    onChange={value => setInvoiceDate(value)}
                    format='DD/MM/YYYY'
                    style={{ width: '100%' }}
                    status={!invoiceDate ? 'error' : ''}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      marginBottom: 4,
                      fontWeight: 500,
                    }}
                  >
                    Total (including VAT){' '}
                    <span style={{ color: '#ff4d4f' }}>*</span>
                  </label>
                  <Input
                    value={invoiceTotalInput}
                    onChange={event => {
                      setInvoiceTotalInput(event.target.value)
                      setInvoiceTotalSource(INVOICE_TOTAL_SOURCE.MANUAL)
                    }}
                    placeholder='e.g. 51.95'
                    status={
                      selectedInvoices?.length > 0 &&
                      parseInvoiceTotalToPennies(invoiceTotalInput) == null
                        ? 'error'
                        : ''
                    }
                  />
                  <p
                    style={{
                      fontSize: 11,
                      color: '#8c8c8c',
                      margin: '6px 0 0',
                    }}
                  >
                    Required when attaching from OMS storage. Local uploads
                    collect this in the confirm step.
                  </p>
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '24px',
                flexWrap: 'wrap',
              }}
            >
              {/* Upload from Local PC */}
              <div
                style={{
                  flex: 1,
                  minWidth: 220,
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: 24,
                  textAlign: 'center',
                }}
              >
                <Upload
                  accept='.pdf,.png,.jpg,.jpeg'
                  multiple
                  showUploadList={false}
                  fileList={[]}
                  beforeUpload={() => false}
                  onChange={handleMultipleFileSelect}
                >
                  <div style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        fontSize: 32,
                        color: '#1890ff',
                      }}
                    >
                      <UploadOutlined />
                    </div>
                  </div>
                  <h3>Upload from Local PC</h3>
                  <p>
                    OMS reads the document and confirms details against this
                    order
                  </p>
                  <Button
                    onClick={() => {
                      setSelectedInvoices([])
                      setLocalPreviews([])
                      setFileDataUrl('')
                      setSelectedFiles([])
                    }}
                    type='primary'
                  >
                    Select Invoice File(s)
                  </Button>
                </Upload>
              </div>
              {/* Select from OMS Storage */}
              <SelectFromOMS
                setSelectedInvoices={setSelectedInvoices}
                onInvoicesConfirmed={applySelectedInvoices}
                defaultSupplierTitle={order?.supplier?.title}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <SelectedPreviews
                fileDataUrl={fileDataUrl}
                selectedInvoices={selectedInvoices}
                localPreviews={localPreviews}
              />
              <Tooltip
                title={isAttachDisabled ? getAttachDisabledReason() : ''}
              >
                <span style={{ display: 'inline-block', marginTop: 24 }}>
                  <Button
                    type='primary'
                    onClick={handleAttachOrReplaceInvoice}
                    disabled={isAttachDisabled}
                  >
                    {isLoading ? (
                      <Spin size='small' />
                    ) : isAdding ? (
                      'Add invoice'
                    ) : order?.attachedInvoicesUrls?.length > 0 ? (
                      'Replace Invoice'
                    ) : (
                      'Attach Invoice'
                    )}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </>
        )}
      </Modal>
      <GoodsReceiptingAttachConfirmModal
        open={confirmUploadOpen}
        order={order}
        orderLines={orderLinesForConfirm}
        files={confirmUploadFiles}
        venueId={venueId}
        filterDates={filterDates}
        appendToExisting={!!isAdding}
        onClose={() => {
          setConfirmUploadOpen(false)
          setConfirmUploadFiles([])
        }}
        onAttached={async () => {
          setConfirmUploadOpen(false)
          setConfirmUploadFiles([])
          setIsReplacing(false)
          setIsAdding(false)
          onClose()
        }}
      />
    </>
  )
}

export default AttachInvoiceModal
